import { ComponentRef, FormEvent, MouseEvent, ReactNode, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, ErrorSummary, Heading, Paragraph, Skeleton } from '@digdir/designsystemet-react';
import { CheckmarkIcon, ShieldLockIcon } from '@navikt/aksel-icons';
import { useGetCustomSurveyByUUIDQuery } from '../../hooks/queries/useGetCustomSurveyByUUIDQuery';
import { useSubmitCustomSurveyMutation } from '../../hooks/mutations/useSubmitCustomSurveyMutation';
import { useLanguageSynchronization } from '../../hooks/useLanguageSynchronization';
import { ErrorBoundary } from '../../components/ErrorBoundary/ErrorBoundary';
import { FormSubmissionUtils } from '../FormSubmission/formSubmission.utils';
import { SurveyStatusEvaluator } from '../../domain/custom-survey';
import type { SubmitCustomSurveyAnswer } from '../../api/customSurveys';
import { TrustpulseLogo } from '../../components/TrustpulseLogo';
import { QuestionField } from './components/QuestionField';
import {
  Answers,
  answeredCount,
  answersToSubmit,
  estimatedMinutes,
  groupQuestions,
  missingAnswers,
  sortQuestions,
} from './surveyFormView';
import classes from './CustomSurveyFormSubmission.module.css';

const LANGUAGES = [
  { code: 'no', lang: 'nb', label: 'Norsk' },
  { code: 'en', lang: 'en', label: 'English' },
] as const;

/** Topp med merke, språkvalg og hvor mye som er besvart, og tonet bue bak innholdet */
const Shell = ({ progress, children }: { progress?: number; children: ReactNode }) => {
  const { t, i18n } = useTranslation();
  const { handleLanguageChange } = useLanguageSynchronization();

  return (
    <div className={classes.page}>
      <a href="#main" className="ds-skip-link">
        {t('customSurveys.form.skipLink')}
      </a>
      <header className={classes.header}>
        <div className={classes.headerInner}>
          <TrustpulseLogo />
          <div role="group" aria-label={t('customSurveys.form.language')} className={classes.languages}>
            {LANGUAGES.map(({ code, lang, label }) => (
              <button
                key={code}
                type="button"
                lang={lang}
                aria-pressed={i18n.language === code}
                className={classes.language}
                onClick={() => handleLanguageChange(code)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        {progress !== undefined && (
          <div aria-hidden="true" className={classes.progressTrack}>
            <div className={classes.progressFill} style={{ width: `${progress * 100}%` }} />
          </div>
        )}
      </header>
      <main id="main" tabIndex={-1} className={classes.main}>
        <div aria-hidden="true" className={classes.backdrop} />
        {children}
      </main>
    </div>
  );
};

/** Takk, utilgjengelig og feil: kort melding midt på siden */
const Message = ({ title, text, success }: { title: string; text: string; success?: boolean }) => (
  <div className={classes.message}>
    {success && (
      <span aria-hidden="true" className={classes.messageIcon}>
        <CheckmarkIcon />
      </span>
    )}
    <Heading level={1} data-size="lg">
      {title}
    </Heading>
    <Paragraph data-size="lg" className={classes.subtle}>
      {text}
    </Paragraph>
  </div>
);

export const CustomSurveyFormSubmission = () => {
  const { t, ready } = useTranslation();
  const { guid } = useParams();
  const { data: survey, error, isLoading } = useGetCustomSurveyByUUIDQuery(guid!);
  const { mutate, isPending, isSuccess, isError: submitFailed } = useSubmitCustomSurveyMutation(guid!);
  const [answers, setAnswers] = useState<Answers>({});
  const [tried, setTried] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const errorSummaryRef = useRef<ComponentRef<typeof ErrorSummary>>(null);

  // Flytt fokus til feiloppsummeringen hver gang innsendingen stoppes
  useEffect(() => {
    if (attempt > 0) errorSummaryRef.current?.focus();
  }, [attempt]);

  useEffect(() => {
    if (isSuccess) window.scrollTo(0, 0);
  }, [isSuccess]);

  if (!ready || !guid) return null;

  if (isLoading) {
    return (
      <Shell>
        <div className={classes.content} aria-busy="true">
          <span className="ds-sr-only">{t('customSurveys.form.loading')}</span>
          <Skeleton variant="rectangle" width="100%" height={180} />
          <Skeleton variant="rectangle" width="100%" height={220} />
          <Skeleton variant="rectangle" width="100%" height={220} />
        </div>
      </Shell>
    );
  }

  if (isSuccess) {
    return (
      <Shell progress={1}>
        <Message success title={t('customSurveys.form.thanks.title')} text={t('customSurveys.form.thanks.text')} />
      </Shell>
    );
  }

  if (error || !survey) {
    return (
      <Shell>
        <Message title={t('customSurveys.form.error.title')} text={t('customSurveys.form.error.text')} />
      </Shell>
    );
  }

  const hasAlreadyAnswered = FormSubmissionUtils.hasAnswered(survey.uuid);
  if (!SurveyStatusEvaluator.isAvailableForSubmission(survey.isActive, hasAlreadyAnswered)) {
    return (
      <Shell>
        <Message
          title={t(`customSurveys.form.unavailable.${hasAlreadyAnswered ? 'answered' : 'closed'}.title`)}
          text={t(`customSurveys.form.unavailable.${hasAlreadyAnswered ? 'answered' : 'closed'}.text`)}
        />
      </Shell>
    );
  }

  const questions = sortQuestions(survey.questions);
  const total = questions.length;
  const numberOf = new Map(questions.map((question, index) => [question.id, index + 1]));
  const missing = tried ? missingAnswers(questions, answers) : [];
  const groups = groupQuestions(questions);
  const hasCategories = groups.some((group) => group.category !== null);
  const answered = answeredCount(questions, answers);

  const setAnswer = (answer: SubmitCustomSurveyAnswer) =>
    setAnswers((previous) => ({ ...previous, [answer.questionId]: answer }));

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    // Knappen deaktiveres ikke mens svaret sendes, så vi stopper dobbel innsending her
    if (isPending) return;
    setTried(true);
    if (missingAnswers(questions, answers).length > 0) {
      setAttempt((count) => count + 1);
      return;
    }
    mutate(
      { answers: answersToSubmit(questions, answers) },
      { onSuccess: () => FormSubmissionUtils.addNewAnsweredSurvey(survey.uuid) },
    );
  };

  const goToQuestion = (id: number) => (event: MouseEvent) => {
    event.preventDefault();
    const element = document.getElementById(`question-${id}`);
    element?.scrollIntoView({ block: 'start' });
    element?.querySelector<HTMLElement>('input, textarea')?.focus({ preventScroll: true });
  };

  const renderQuestion = (question: (typeof questions)[number]) => (
    <QuestionField
      key={question.id}
      question={question}
      number={numberOf.get(question.id) ?? 0}
      total={total}
      answer={answers[question.id]}
      showError={missing.some((item) => item.id === question.id)}
      onChange={setAnswer}
    />
  );

  return (
    <ErrorBoundary>
      <Shell progress={total > 0 ? answered / total : 0}>
        <div className={classes.content}>
          <div className={classes.intro}>
            <span className={classes.eyebrow}>{t('customSurveys.form.eyebrow')}</span>
            <Heading level={1} data-size="xl" className={classes.title}>
              {survey.title}
            </Heading>
            {survey.description && <Paragraph className={classes.description}>{survey.description}</Paragraph>}
            <Paragraph data-size="sm" className={classes.subtle}>
              {t('customSurveys.form.meta', {
                questions: t('customSurveys.form.questionCount', { count: total }),
                time: t('customSurveys.form.minutes', { count: estimatedMinutes(questions) }),
              })}
            </Paragraph>
            <div className={classes.privacy}>
              <ShieldLockIcon aria-hidden className={classes.privacyIcon} />
              <div>
                <Heading level={2} data-size="2xs">
                  {t('customSurveys.form.privacy.title')}
                </Heading>
                <Paragraph data-size="sm">{t('customSurveys.form.privacy.text')}</Paragraph>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} noValidate className={classes.form}>
            {missing.length > 0 && (
              <ErrorSummary ref={errorSummaryRef} tabIndex={-1}>
                <ErrorSummary.Heading>{t('customSurveys.form.errors.heading')}</ErrorSummary.Heading>
                <ErrorSummary.List>
                  {missing.map((question) => (
                    <ErrorSummary.Item key={question.id}>
                      <ErrorSummary.Link href={`#question-${question.id}`} onClick={goToQuestion(question.id)}>
                        {t('customSurveys.form.errors.link', {
                          number: numberOf.get(question.id),
                          question: question.questionText,
                        })}
                      </ErrorSummary.Link>
                    </ErrorSummary.Item>
                  ))}
                </ErrorSummary.List>
              </ErrorSummary>
            )}

            {hasCategories
              ? groups.map((group, index) => (
                  <section key={group.category ?? 'other'} aria-labelledby={`group-${index}`} className={classes.group}>
                    <div className={classes.groupHeader}>
                      <Heading level={2} data-size="xs" id={`group-${index}`}>
                        {group.category ?? t('customSurveys.form.otherQuestions')}
                      </Heading>
                      <span className={classes.subtle}>
                        {t('customSurveys.form.questionCount', { count: group.questions.length })}
                      </span>
                    </div>
                    {group.questions.map(renderQuestion)}
                  </section>
                ))
              : questions.map(renderQuestion)}

            <div className={classes.submit}>
              <Paragraph data-size="sm" role="status" className={classes.subtle}>
                {t('customSurveys.form.progress', { count: answered, total })}
              </Paragraph>
              {submitFailed && (
                <Paragraph data-size="sm" className={classes.submitError}>
                  {t('customSurveys.form.submitError')}
                </Paragraph>
              )}
              <Button type="submit" data-size="lg" aria-busy={isPending || undefined} className={classes.submitButton}>
                {isPending ? t('customSurveys.form.submitting') : t('customSurveys.form.submit')}
              </Button>
            </div>
          </form>
        </div>
      </Shell>
    </ErrorBoundary>
  );
};
